import * as RadixPopover from '@radix-ui/react-popover';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { DropdownProps } from './Dropdown.types';
import { DropdownContext, type DropdownContextValue } from './DropdownContext';

/**
 * Compound Dropdown root — **web** implementation.
 *
 * Holds the open state, mounts the Radix `Popover.Root`, and provides context
 * to the compound parts: `<DropdownTrigger>` renders the anchored trigger,
 * `<DropdownContent>` the portaled menu panel, `<DropdownItem>` the rows.
 *
 * The native counterpart at `Dropdown.native.tsx` opens a `@gorhom/bottom-sheet`.
 */
export function Dropdown({ children, size = 'm', onOpenChange, testID }: DropdownProps) {
  const [open, setOpen] = useState(false);
  // Ref'd trigger wrapper — Radix positions relative to it, and DropdownContent's
  // `onInteractOutside` guard checks whether a dismiss originated inside it.
  const anchorRef = useRef<HTMLSpanElement>(null);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const ctx = useMemo<DropdownContextValue>(
    () => ({
      open,
      toggle: () => handleOpenChange(!open),
      close: () => handleOpenChange(false),
      size,
      testID,
      anchorRef,
    }),
    [open, handleOpenChange, size, testID],
  );

  return (
    <DropdownContext.Provider value={ctx}>
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange} modal={false}>
        {children}
      </RadixPopover.Root>
    </DropdownContext.Provider>
  );
}
